import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, TextField,
  Button, Alert, Typography, Paper, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import {
  ContentCopy as CopyIcon, CheckCircle as CheckIcon,
  AppRegistration as RegisterIcon, SendToMobile as MobileIcon,
} from '@mui/icons-material';
import AmountField, { formatAmount } from './AmountField';

const AUTO_POLL_INTERVAL_MS = 5000;
const AUTO_POLL_MAX_ATTEMPTS = 12; // ~60s
const DEFAULT_REASON = 'School Fees Payment';

/**
 * Presentational adhoc-payment dialog shared by the Student Portal "Pay Fees"
 * flow and the staff Student Ledger "Collect via SchoolPay" flow. Has no
 * knowledge of which context it's used in — identity/balance arrive as props,
 * submission/polling are delegated to the caller via onSubmit/onCheckStatus.
 *
 * Supports two modes: "request" pushes a mobile money prompt immediately;
 * "register" generates a reference the payer completes later via mobile
 * money, bank, or a SchoolPay agent — no phone required, no auto-polling
 * (the payer may complete it hours or days later).
 */
const AdhocPaymentDialog = ({ open, onClose, payerName, balance, onSubmit, onCheckStatus, onPaid }) => {
  const [step, setStep] = useState('form'); // form | pending | paid
  const [mode, setMode] = useState('request'); // request | register
  const [phoneNumber, setPhoneNumber] = useState('');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState(DEFAULT_REASON);
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [error, setError] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [copiedRef, setCopiedRef] = useState(false);

  const pollRef = useRef({ timer: null, attempts: 0 });

  useEffect(() => {
    const poll = pollRef.current;
    return () => clearTimeout(poll.timer);
  }, []);

  useEffect(() => {
    if (open) {
      setStep('form');
      setMode('request');
      setPhoneNumber('');
      setAmount(balance ? String(balance) : '');
      setReason(DEFAULT_REASON);
      setError('');
      setPaymentReference('');
    } else {
      clearTimeout(pollRef.current.timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, balance]);

  const handleCopyReference = (reference) => {
    navigator.clipboard.writeText(reference);
    setCopiedRef(true);
    setTimeout(() => setCopiedRef(false), 2000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (mode === 'request' && !phoneNumber.trim()) {
      setError('Please enter the mobile money phone number.');
      return;
    }
    const amountNumber = Number(amount);
    if (!amountNumber || amountNumber <= 0) {
      setError('Please enter a valid amount.');
      return;
    }

    setLoading(true);
    try {
      const { payment_reference } = await onSubmit(mode, phoneNumber.trim(), amountNumber, reason.trim() || DEFAULT_REASON);
      setPaymentReference(payment_reference);
      setStep('pending');
      if (mode === 'request') {
        startAutoPoll(payment_reference);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Could not start the payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const checkStatus = async (reference) => {
    setCheckingStatus(true);
    try {
      const { status, balance: newBalance } = await onCheckStatus(reference);
      if (status === 'PAID') {
        setStep('paid');
        clearTimeout(pollRef.current.timer);
        onPaid?.(newBalance);
        return true;
      }
    } catch {
      // ignore transient poll errors
    } finally {
      setCheckingStatus(false);
    }
    return false;
  };

  const startAutoPoll = (reference) => {
    pollRef.current.attempts = 0;
    const tick = async () => {
      pollRef.current.attempts += 1;
      const paid = await checkStatus(reference);
      if (!paid && pollRef.current.attempts < AUTO_POLL_MAX_ATTEMPTS) {
        pollRef.current.timer = setTimeout(tick, AUTO_POLL_INTERVAL_MS);
      }
    };
    pollRef.current.timer = setTimeout(tick, AUTO_POLL_INTERVAL_MS);
  };

  const ReferenceBox = ({ reference }) => (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
      <Typography variant="caption" color="text.secondary">Payment Reference</Typography>
      <Box display="flex" alignItems="center" gap={1} mt={0.5}>
        <Typography variant="h6" sx={{ fontFamily: 'monospace', flex: 1 }}>{reference}</Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={copiedRef ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
          onClick={() => handleCopyReference(reference)}
        >
          {copiedRef ? 'Copied' : 'Copy'}
        </Button>
      </Box>
    </Paper>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, fontSize: '1.25rem', pt: 3, px: 3 }}>Pay School Fees</DialogTitle>
      <DialogContent dividers sx={{ p: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {step === 'form' && (
          <Box component="form" id="adhoc-payment-form" onSubmit={handleSubmit}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>{payerName}</strong><br />
              Outstanding balance: {formatAmount(balance)}
            </Alert>

            <ToggleButtonGroup
              value={mode}
              exclusive
              onChange={(e, val) => { if (val) setMode(val); }}
              fullWidth
              sx={{ mb: 2.5 }}
            >
              <ToggleButton value="request" sx={{ py: 1.25 }}>
                <MobileIcon sx={{ mr: 0.75, fontSize: 18 }} /> Push to Phone (Pay Now)
              </ToggleButton>
              <ToggleButton value="register" sx={{ py: 1.25 }}>
                <RegisterIcon sx={{ mr: 0.75, fontSize: 18 }} /> Generate Reference (Pay Later)
              </ToggleButton>
            </ToggleButtonGroup>

            {mode === 'request' && (
              <TextField
                fullWidth margin="normal" label="Mobile Money Phone Number"
                placeholder="0772123456"
                value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)}
                required
                helperText="The payer will receive a debit prompt on this number"
              />
            )}
            <AmountField
              label="Amount to Pay"
              value={amount}
              onChange={setAmount}
              required
              helperText={`Up to ${formatAmount(balance)}`}
            />
            <TextField
              fullWidth margin="normal" label="Reason"
              value={reason} onChange={(e) => setReason(e.target.value)}
            />
          </Box>
        )}

        {step === 'pending' && mode === 'request' && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              A payment prompt was sent to <strong>{phoneNumber}</strong>. Enter the Mobile
              Money PIN to approve the payment.
            </Alert>
            <ReferenceBox reference={paymentReference} />
          </Box>
        )}

        {step === 'pending' && mode === 'register' && (
          <Box>
            <Alert severity="success" icon={<CheckIcon />} sx={{ mb: 2 }}>
              A payment reference has been generated for {payerName}.
            </Alert>
            <ReferenceBox reference={paymentReference} />
            <Typography variant="body2" color="text.secondary">
              Give this reference to {payerName} — they can complete the payment via mobile
              money, bank, or a SchoolPay agent by quoting this code. Nothing is charged
              until they do.
            </Typography>
          </Box>
        )}

        {step === 'paid' && (
          <Alert severity="success">Payment received. Thank you!</Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} size="large">Close</Button>
        {step === 'form' && (
          <Button type="submit" form="adhoc-payment-form" variant="contained" size="large" disabled={loading}>
            {loading ? 'Sending...' : (mode === 'request' ? 'Pay Now' : 'Generate Reference')}
          </Button>
        )}
        {step === 'pending' && (
          <Button variant="contained" size="large" onClick={() => checkStatus(paymentReference)} disabled={checkingStatus}>
            {checkingStatus ? 'Checking...' : 'Check Status'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AdhocPaymentDialog;
